import type { CreateUniLoginHostedAuthRequestResponse } from '@unipile/types';
import { Puzzle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProvider } from '../../../hooks/useProvider';
import type { ExtensionBrowser } from '../../../libs/unilogin-bridge';
import { Button } from '../../shadcn/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../shadcn/Dialog';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '../../shadcn/Empty';

/** No extension answered: offers the install link for this browser, or asks for a desktop browser. */
export function UniLoginExtensionMissing({
  browser,
  request,
}: {
  browser: ExtensionBrowser | null;
  request: CreateUniLoginHostedAuthRequestResponse | undefined;
}) {
  const { t } = useTranslation();
  const { provider_name } = useProvider();
  const installUrl = selectExtensionInstallUrl(request, browser);

  return (
    <Empty className="h-56 flex-none gap-4 border p-4 md:p-4">
      <EmptyHeader className="min-h-0 flex-1 justify-center gap-1">
        <EmptyMedia variant="icon">
          <Puzzle />
        </EmptyMedia>
        <EmptyTitle className="text-sm">
          {t(browser ? 'LoginScreen.unilogin.extension_step_title' : 'LoginScreen.unilogin.unsupported_browser_title', {
            ns: 'translation',
            provider: provider_name,
          })}
        </EmptyTitle>
        <EmptyDescription>
          {t(browser ? 'LoginScreen.unilogin.extension_not_detected' : 'LoginScreen.unilogin.unsupported_browser_description', {
            ns: 'translation',
            provider: provider_name,
          })}
        </EmptyDescription>
      </EmptyHeader>
      {browser && installUrl && (
        <EmptyContent className="shrink-0 gap-1">
          <Button asChild size="sm" variant="outline">
            <a href={installUrl} target="_blank" rel="noreferrer">
              {t(
                isManualExtensionInstall(browser)
                  ? 'LoginScreen.unilogin.download_extension_for'
                  : 'LoginScreen.unilogin.install_for',
                { ns: 'translation', provider: provider_name, browser: extensionBrowserLabel(browser) },
              )}
            </a>
          </Button>
          {isManualExtensionInstall(browser) && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="h-auto px-0 py-1" variant="link" type="button">
                  {t('LoginScreen.unilogin.installation_details', { ns: 'translation' })}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('LoginScreen.unilogin.manual_install_title', { ns: 'translation' })}</DialogTitle>
                </DialogHeader>
                <ManualExtensionInstallGuide browser={browser} />
              </DialogContent>
            </Dialog>
          )}
        </EmptyContent>
      )}
    </Empty>
  );
}

function ManualExtensionInstallGuide({ browser }: { browser: 'edge' | 'safari' }) {
  const { t } = useTranslation();

  return (
    <ol className="text-muted-foreground list-decimal space-y-1.5 pl-5 text-sm leading-6">
      {[1, 2, 3, 4].map((step) => (
        <li key={step}>{t(`LoginScreen.unilogin.${browser}_install_step_${step}`, { ns: 'translation' })}</li>
      ))}
    </ol>
  );
}

function selectExtensionInstallUrl(
  request: CreateUniLoginHostedAuthRequestResponse | undefined,
  browser: ExtensionBrowser | null,
): string | undefined {
  if (!request || !browser) return undefined;
  if (browser === 'edge' || browser === 'safari') return request.extension_download_urls?.[browser];
  if (browser === 'chrome') return request.extension_store_urls?.chrome;
  return request.extension_store_urls?.firefox;
}

function isManualExtensionInstall(browser: ExtensionBrowser): browser is 'edge' | 'safari' {
  return browser === 'edge' || browser === 'safari';
}

function extensionBrowserLabel(browser: ExtensionBrowser): string {
  if (browser === 'edge') return 'Edge';
  if (browser === 'firefox') return 'Firefox';
  if (browser === 'safari') return 'Safari';
  return 'Chrome';
}
